'use client';

import { useQuery } from '@tanstack/react-query';

import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2';

/**
 * Figma „Account“ (from `lg`; below it the mobile navbar): always a link to the profile page, which
 * shows the sign-in page to a guest and the profile to a signed-in customer; the label („Prihlásiť sa“
 * / „Môj účet“) shows from `xl`.
 */
export function HeaderAccount() {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const label = me ? t('text197') : t('text189');

  return (
    <Link
      href={ROUTES.PROFILE() ?? '#'}
      prefetch={false}
      aria-label={label}
      className={`flex h-[46px] min-w-11 items-center justify-center gap-2 rounded-[18px] text-[#0F1012] ${FOCUS}`}
    >
      <Icon name="user" size={24} className="text-[#D7191F]" />
      <span className="dlz-body-s-medium hidden whitespace-nowrap xl:inline">{label}</span>
    </Link>
  );
}

/**
 * Figma „Cart“ (from `lg`): icon with the item count and the total (core header-cart-button – same
 * cart query, VAT setting and price format).
 */
export function HeaderCart() {
  const systemLabels = useSystemLabels();
  const { data: cart } = useQuery(useCartQueryOpts());
  const { currency, currency_settings: currencySettings } = useGlobalStore(s => s.generalLayout);
  const withoutVat = useGlobalStore(
    s => s.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );
  const count = cart?.count ?? 0;
  const total = withoutVat ? cart?.items_total_without_vat : cart?.items_total;
  const displayCount = count > 99 ? '99+' : count;
  const displayTotal = formatPrice(total ?? 0, currency, currencySettings);

  return (
    <Link
      href={ROUTES.CART() ?? '#'}
      prefetch={false}
      aria-label={`${displayCount} ${displayTotal} – ${systemLabels('cart')}`}
      className={`flex h-[46px] shrink-0 items-center justify-center gap-3 rounded-[18px] border border-[#E2E4E7] px-4 text-[#0F1012] ${FOCUS}`}
    >
      <span className="relative">
        <Icon name="shopping-cart" size={24} className="text-[#D7191F]" />
        <span
          aria-hidden="true"
          className="absolute -top-1.5 left-3.5 flex size-[18px] items-center justify-center rounded-full bg-[#D7191F] text-[11px] font-bold leading-none text-white"
        >
          {displayCount}
        </span>
      </span>
      <span className="dlz-body-s-medium whitespace-nowrap">{displayTotal}</span>
    </Link>
  );
}
